/* eslint-disble react/jsx-sort-props */
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Layout } from '@abyss/web/ui/Layout';
import { ResultCount } from '@abyss/web/ui/Pagination';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import { Text } from '@abyss/web/ui/Text';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import { CompareShareToggle } from '../../../../common/CompareShareToggle';
import {
  AdobeEventMethods,
  Constants,
  ProviderTypes,
  ReverseCoverageTypesCodes,
  SHOULD_GET_HG_DATA,
} from '../../../../common/Constants';
import { FeatureFlags } from '../../../../common/ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { mobileOnly, phoneOnly } from '../../../../common/ConstantsStyles';
import { ResultSort } from '../../../../common/ContentFilters/ResultSort';
import { DataCardContainer } from '../../../../common/DataCard/DataCardContainer';
import { MapDisplay } from '../../../../common/MapDisplay';
import { MapViewGroupedLocationDrawer } from '../../../../common/MapViewGroupedLocationDrawer/MapViewGroupedLocationDrawer';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { SmartChoiceInfoChip } from '../../../../common/PSXHeader/SmartChoice/SmartChoiceInfoChip';
import { getFeatureFlag } from '../../../../common/Utils';
import {
  convertCoverageType,
  convertProviderTypeToAdobeType,
  getIndicatorsForImpressions,
  getTier1Indicator,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import {
  isValidCareCategory,
  usePopoverContentBySectionType,
} from '../../../../common/Utils/getProviderType';
import { isUmrMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { CountySearchContext } from '../../../../context/CountySearchContext';
import { PortalContext } from '../../../../context/PortalContext';
import { useAdobePageTrackEvent } from '../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  useProviderSearchV2,
  useProviderSummaryVars,
} from '../../../../hooks/useProviderSearch';
import { useProviderSearchTelemetry } from '../../../../hooks/useProviderSearchTelemetry';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { BreadcrumbSessionStorage } from '../../../../models/BreadcrumbSessionStorage';
import { CompareProvider, Provider } from '../../../../models/Provider';
import { ResponseHeaders } from '../../../../models/ResponseHeaders';
import { Directions } from '../../../../models/RouteDirections';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { handleMapClickLocation } from '../../../../utils/directions.utils';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import {
  getDataCardMinHeight,
  getFinalProviderResults,
  getProviderForPinDetails,
  getSidePanelArrowIcon,
  mapViewUpdatePin,
} from '../../../../utils/map.utils';
import { getOhbsPolicyKey } from '../../../../utils/ohbs/ohbs.utils';
import {
  getProviderTypeFromProviderSummaryType,
  getSuppressFacilityFlags,
} from '../../../../utils/providerDetails.utils';
import { loginNotRequired } from '../../../../utils/session.utils';
import {
  getCurrentMember,
  getLoggedInMember,
} from '../../../../utils/user.utils';
import { useConfig } from '../../context/Analytics';
import { DownloadResultsButton } from '../ViewAll/DownloadResultsButton';
import { ShareCompareButton } from '../ViewAll/ShareCompareButton';
import { ShowCompareFloatingSection } from '../ViewAll/ShowCompareFloatingSection';
import { AllPaginationProps } from './AllPaginationProps';
import { BottomDataCard } from './BottomDataCard';
import { CompareDrawerProps } from './CompareDrawerProps';
import { DataCardList, DataCardListStyled } from './DataCardList';
import {
  EnhancePaginationStyles,
  EnhanceStackWrapper,
  ListWrap,
  ListWrapColumn,
  MapDataPagination,
  MapStyleWrap,
  MapViewWrap,
  MapviewWrapper,
  dataCardCssWidth,
} from './MapView.styled';
import { MapViewHeader } from './MapViewHeader';
import { MapViewHeaderEnhancement } from './MapViewHeaderEnhancement';
import { MapViewTopPagination } from './MapViewTopPagination';
import { MobileMapFilter } from './MobileMapFilter';
import { Popup } from './Popup';
import { FilterProviderResultsPrintViewProps } from './Print/FilterProviderResultsPrintView';
import { SidePaneCloseButton } from './SidePaneCloseButton';
import {
  concatenateStrings,
  getActionStr,
  getEnableMapPinRedesign,
  getOpenCompareShare,
  getTelemetryCoverageType,
  getTranslatedText,
} from './utils';
import { VirtualCareProvidersBanner } from './VirtualCareProvidersBanner/VirtualCareProvidersBanner';

type Props = {
  previousLocation: string;
  setPreviousLocation: (a: string) => void;
  allPaginationProps: AllPaginationProps;
  compareDrawerProps: CompareDrawerProps;
  headers: ResponseHeaders;
  isLoading: boolean;
  isPageNav?: boolean;
  openShare?: boolean;
  results: any[];
  sectionType?: string;
  selectedCheckbox?: any;
  totalResultsCount?: number;
  addSelectedProvider: (a: any) => void;
  setOpenMapview: (a: boolean) => void;
  setMobileRouteView: (a: boolean) => void;
  mobileRouteView: boolean;
  setOpenShare: (a: boolean) => void;
  setSelectedCheckbox: (a: { checked: {} }) => void;
  setSelectedItems: (a: CompareProvider[]) => void;
  setOpenBoxContents: any;
  search?: string | null;
  selectedFilters?: string;
  providerType?: string;
  providerPins?: Provider[];
  searchTerm?: string;
  navigateToDirections?: boolean;
  setNavigateToDirections: (a: boolean) => void;
  openMapview: boolean;
  coverageType: string;
  directions?: Directions;
  setDirections: (a: Directions) => void;
  routeEndCoords?: [number | null, number | null];
  setRouteEndCoords(coords: [number | null, number | null]): void;
  searchMethod?: string;
  isMNR?: boolean;
  heading?: string;
  enableMapViewEnhancements: boolean;
  choosePCP: boolean;
  filterProviderResultsPrintViewProps?: FilterProviderResultsPrintViewProps;
  isTieredPlan?: boolean;
  enableMapFilterSortCTA: boolean;
  searchRadius?: string | number;
  compareShareToggle: boolean;
  setCompareShareToggle: (a: boolean) => void;
  expandedProviderListView: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  items: CompareProvider[];
  setItems: (a: CompareProvider[]) => void;
  removeItem: (a: CompareProvider) => void;
  showMergedCompareAndShare: boolean;
  setExpandedProviderListView: (a: boolean) => void;
  setIsCompareClicked: () => void;
  chipValue?: string | undefined;
  setIsVirtualCareListView: (a: boolean) => void;
  isVirtualCareListView?: boolean;
  recommendationPreferences?: any;
};

const getPageHeader = (
  t: (content: string) => string,
  heading: string | undefined,
  search: string | null | undefined,
  sectionType: string
) => {
  const translatedText = heading?.length
    ? t(`${heading}`)
    : t(`${search}`) || '';

  return `${t(`${sectionType}-results-for`)} ${translatedText}`;
};

export const MapView = ({
  containerRef,
  items,
  setItems,
  removeItem,
  showMergedCompareAndShare,
  previousLocation,
  setPreviousLocation,
  allPaginationProps,
  compareDrawerProps,
  headers,
  heading,
  isLoading = false,
  choosePCP,
  isPageNav,
  openShare = false,
  results = [],
  sectionType = '',
  selectedCheckbox = { checked: {} },
  addSelectedProvider,
  setOpenMapview,
  setMobileRouteView,
  mobileRouteView,
  setOpenShare,
  setSelectedCheckbox,
  setSelectedItems,
  setOpenBoxContents,
  search,
  selectedFilters,
  providerPins,
  providerType,
  searchTerm,
  navigateToDirections = false,
  setNavigateToDirections,
  openMapview = false,
  coverageType,
  directions,
  setDirections,
  setRouteEndCoords,
  routeEndCoords = [null, null],
  searchMethod,
  totalResultsCount,
  isMNR,
  enableMapViewEnhancements,
  enableMapFilterSortCTA,
  filterProviderResultsPrintViewProps,
  isTieredPlan,
  searchRadius,
  compareShareToggle,
  setCompareShareToggle,
  expandedProviderListView,
  setExpandedProviderListView,
  setIsCompareClicked,
  chipValue,
  setIsVirtualCareListView,
  recommendationPreferences,
}: Props) => {
  const [
    showMapCollapse,
    enableMapPinRedesignFlag,
    enableMapViewHideOption,
    enableListViewHeaderSticky,
    enableFontHeaderMapView,
    enableVirtualCareEpic,
    enableVirtualCareProvidersInMapResultPage,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SHOW_MAP_COLLAPSE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_HEADER_STICKY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FONT_HEADER_MAP_VIEW,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_PROVIDERS_MAP_RESULT_PAGE,
  ]);

  const sessionData = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.LAST_SELECTED_FROM_DRAWER
  );
  const [breadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );
  const [isOpen, setIsOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [navigatedFromDetails, setNavigatedFromDetails] = useState(false);

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );
  const enableMapPinRedesign: boolean = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );
  const {
    selectedItems = [],
    map,
    openCompare,
    setOpenCompare,
  } = compareDrawerProps;

  const listRef = useRef<HTMLDivElement>(null);
  const topPaginationRef = useRef<HTMLDivElement>(null);
  const selectedProviderId = useRef<string | null>(null);
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();

  const [searchParams] = useSearchParams();

  const storedMembers = useStore(StoreKeys.OBAPI_MEMBERS);
  const isBHCare: boolean = coverageType?.includes(
    ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
  );

  const isOHBS = useMemo(() => {
    const obhCoverage = getOhbsPolicyKey(storedMembers);
    return !!(obhCoverage && isBHCare);
  }, [isBHCare, storedMembers]);

  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [isPageTracked, setPageTracked] = useState<boolean>(false);
  const [closeSidePanel, setCloseSidePanel] = useState<boolean>(false);
  const [doPopupUpdate, setDoPopupUpdate] = useState<boolean>(false);
  const [popupContent, setPopupContent] = useState<any>('');
  const [providerSummaryVars] = useState(useProviderSummaryVars(isOHBS));
  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  const { longitude, latitude, name } = getGeoLocationFromStorage();

  const memberHasRPK = useStore(StoreKeys.TIER1_PLAN_FLAG);
  const ddpCodeUser = getLoggedInMember()?.ddpCode;
  const suppressFacilityFlags = getSuppressFacilityFlags();
  const { isCountySearch } = useContext(CountySearchContext);
  const showTierProviderTag = getFeatureFlag(
    featureFlags,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE
  );
  const isValidCategory = isValidCareCategory(chipValue, sectionType);
  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );

  const isSearchRelevancyEventTrackingEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD
  );

  const virtualCareProviders = useVirtualCareStore(
    (state) => state.virtualCareProviders
  );

  const isStandalone = loginNotRequired();
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  const urlCheck = !(isStandalone || isUmr);
  const isSmartChoice = useSmartChoice();
  const getPageTrackEventData = () => {
    const currentMember = getCurrentMember();
    const isFromMixedResults = !!searchTerm;
    const pageTrackEvent = {
      pageName: navigateToDirections ? 'locations:view on map' : 'overview',
      sitesectionLevel1: Constants.ADOBE_TRACKING.VIEWALL_SITESECTION1,
      sitesectionLevel2: `${convertProviderTypeToAdobeType(
        providerType
      )} results`,
      providerBlock: {
        type: convertCoverageType(coverageType),
      },
      impressionBlock: {
        type: 'provider',
        indicator: getIndicatorsForImpressions(
          results,
          featureFlags,
          false,
          {
            suppressPremiumCare,
            ddpCodeUser,
            suppressFacilityFlags,
            memberHasRPK,
            isCountySearch,
          },
          currentMember
        ),
        ...(isSmartChoice && {
          message: 'smart choice displayed',
        }),
        providerTier: getTier1Indicator(showTierProviderTag, results),
      },
    };

    // We want the search data block if coming from mixed results or when we change pages.
    // The !isPageTracked is to guard against it being included when we apply filters.
    // In that case searchModified is already posted which has the search data.
    if ((isFromMixedResults && !isPageTracked) || isPageNav) {
      return {
        ...pageTrackEvent,
        searchBlock: {
          term:
            !searchMethod || searchMethod === AdobeEventMethods.Guided
              ? ''
              : searchTerm,
          type: 'provider',
          filters: selectedFilters ?? '',
          results: String(totalResultsCount ?? ''),
          method: searchMethod?.toLowerCase() ?? 'guided',
        },
      };
    } else {
      return pageTrackEvent;
    }
  };

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [, setCurrentPageDetails] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CURRENT_PAGE_DETAILS,
    {}
  );

  const hitProviderSearchTelemetry = async () => {
    const filteredResults = results?.map((item) => ({
      resultId: item?.providerId,
      resultType:
        item?.providerType?.toLowerCase() === ProviderTypes?.PRACTITIONER
          ? 'PROVIDER'
          : item?.providerType?.toLowerCase() === ProviderTypes.MEDICAL_GROUP
          ? 'GROUP'
          : 'FACILITY',
      name: item?.providerName,
      specialty: item?.speciality,
      address: concatenateStrings(
        item?.address?.line?.join(''),
        item?.address?.city,
        item?.address?.district,
        item?.address?.state,
        item?.address?.postalCode
      ),
      coverageType: Array.isArray(coverageType)
        ? coverageType?.map(getTelemetryCoverageType)
        : getTelemetryCoverageType(coverageType),
      rating: parseFloat(item?.healthGradeRating) ?? 0.0,
      numRatings: item?.surveyCount,
      inNetworkLabel: item?.networkStatus,
      costIndicationLabel: item?.costIndicator,
    }));
    const telemetryVariables = {
      correlationId: correlationIDSessionStorage,
      timestamp: new Date().toISOString(),
      searchQuery: searchTerm ?? '',
      searchLocation: {
        locationInputField: name,
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: searchRadius,
      },
      interpretedAsNameSearch: searchParams.get('searchType') !== 'speciality',
      viewType: 'map',
      results: filteredResults,
      resultPage: pageNumber || 1,
      pageSize,
      resultedInNullSearch: filteredResults?.length === 0,
      searchPlatform: 'web',
      event: 'map',
    };
    setCurrentPageDetails({
      pageNumber: pageNumber || 1,
      pageSize,
    });
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    if (
      (searchParams.get('psx-source')?.includes('auto-complete') ||
        searchParams.get('psx-source') === 'common-searches') &&
      !isLoading &&
      results?.length > 0 &&
      isSearchRelevancyEventTrackingEnabled
    ) {
      hitProviderSearchTelemetry();
    }
  }, [results, isLoading]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent(
    getPageTrackEventData()
  );

  const {
    pageSize,
    pageSizeOptions,
    paginationProps,
    nextPage,
    gotoPage,
    previousPage,
    setPageSize,
    setLastPageSize,
    pageNumber,
  } = allPaginationProps;

  const mapViewViewAllLinkTrack = (name: String, loc: String) => {
    adobeLinkTrackEvent({
      name,
      location: loc,
      type: 'internal',
      destinationUrl: '',
    });
  };
  const nextPageTop = () => {
    mapViewViewAllLinkTrack(
      'next page',
      `body:top pagination control:page ${pageNumber}`
    );
    nextPage();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };
  const nextPageBottom = () => {
    mapViewViewAllLinkTrack(
      'next page',
      `body:bottom pagination control:page ${pageNumber}`
    );
    nextPage();
    topPaginationRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const gotoPageFunc = (page) => {
    mapViewViewAllLinkTrack(
      `page ${page + 1}`,
      `body:bottom pagination control:page ${pageNumber}`
    );
    gotoPage(page);
    topPaginationRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const previousPageTop = () => {
    mapViewViewAllLinkTrack(
      'previous page',
      `body:top pagination control:page ${pageNumber}`
    );
    previousPage();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };
  const previousPageBottom = () => {
    mapViewViewAllLinkTrack(
      'previous page',
      `body:bottom pagination control:page ${pageNumber}`
    );
    previousPage();
    topPaginationRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const setPageSizeFunc = (pageSizeValue) => {
    mapViewViewAllLinkTrack(
      `results per page:${pageSizeValue}`,
      `body:top pagination control:page ${pageNumber}`
    );
    setLastPageSize(pageSize);
    setPageSize(pageSizeValue);
    gotoPage(0);
  };
  const sidePanelArrowIcon = getSidePanelArrowIcon(closeSidePanel);

  const mediaQuery = enableMapViewEnhancements ? mobileOnly : phoneOnly;
  const mobileScreen = useMediaQuery(mediaQuery);
  const dataCardMinHeight = getDataCardMinHeight(mobileScreen);

  const isOpenCompareShare = getOpenCompareShare(
    Boolean(openCompare),
    Boolean(openShare)
  );

  const eapCodeFlag = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.EAP_CODE
  );

  const [selectedId, setSelectedId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );

  useEffect(() => {
    setSelectedId(null);
  }, []);

  const providerLocationId = useMemo(
    () =>
      results?.length > 0
        ? JSON.stringify(results?.map((x) => x.locationId))
        : '',
    [results]
  );

  useEffect(() => {
    if (providerLocationId) {
      adobePageTrackEvent();
      setPageTracked(true);
    }
  }, [providerLocationId]);

  useEffect(() => {
    if (listRef?.current) {
      listRef.current.scrollTop = 0;
    }

    updatePin(null, false);
  }, [results]);

  useEffect(() => {
    window?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    updatePin(null, false);
  }, [compareShareToggle]);

  const [mobileMixedResult, setMobileMixedResult] = useState(false);
  useEffect(() => {
    if (mobileScreen && !openMapview) {
      setMobileMixedResult(true);
    }
    if (mobileScreen && openMapview) {
      setPopupContent(null);
      setMobileMixedResult(false);
    }
    if (!mobileScreen) {
      setMobileRouteView(false);
      setMobileMixedResult(false);
    }
  }, [mobileScreen, openMapview]);

  const { t } = useTranslation();
  const pageHeader = getPageHeader(t, heading, search, sectionType);
  const mapViewSectionType = sectionType?.toLowerCase();
  const toggleSidePanel = () => {
    const actionStr = getActionStr(closeSidePanel);
    adobeLinkTrackEvent({
      name: `sidepanel:${actionStr}`,
      location: `body:map`,
      type: 'internal',
    });
    setCloseSidePanel(!closeSidePanel);
  };

  const [providerResult, getProviderResult] = useProviderSearchV2({});
  const headingPaginationDisabled = `${totalResultsCount} ${t(
    `${mapViewSectionType} results for`
  )}`;

  const searchTermText = (
    <span
      data-testid="results-heading"
      style={{ fontWeight: '700', fontSize: '16px!important' }}
    >
      {' '}
      {heading || search}{' '}
    </span>
  );
  const popoverContent = usePopoverContentBySectionType(sectionType);

  const mapViewHeaderLabel = (
    <span>
      {isValidCategory && (
        <SmartChoiceInfoChip
          popoverContent={popoverContent ?? undefined}
          wrapperStyle={{ paddingBottom: '16px' }}
        />
      )}
      <Heading
        color="$foreground"
        css={{
          '&.abyss-heading-root': {
            fontSize: '16px',
            fontWeight: '400',
            fontFamily: '$primary',
            lineHeight: '20px',
          },
        }}
        offset={1}
      >
        {headingPaginationDisabled}
        {searchTermText}
      </Heading>
    </span>
  );
  useEffect(() => {
    const provider = getProviderForPinDetails(
      providerResult,
      providerType,
      latitude,
      longitude
    );
    const isProviderResultLoaded =
      provider?.providerId === selectedProviderId.current;
    if (doPopupUpdate && isProviderResultLoaded) {
      setDoPopupUpdate(false);
      mapViewUpdatePin(provider, false, popupContent, setPopupContent, map);
    }
  }, [JSON.stringify(providerResult.data), doPopupUpdate]);

  const updatePin = (practitioner, flyToPin = false) => {
    const providerId = practitioner?.providerId;
    selectedProviderId.current = providerId;
    const practitionerInResults = results?.find(
      (result) => result.providerId === providerId
    );

    if (enableMapPinRedesign && !practitionerInResults) {
      if (!practitioner) {
        setDoPopupUpdate(false);
        mapViewUpdatePin(null, false, popupContent, setPopupContent, map);
        return;
      }

      setDoPopupUpdate(true);

      const variables = {
        ...providerSummaryVars,
        providerId: practitioner.providerId,
        locationId: practitioner.locationId,
        providerType: getProviderTypeFromProviderSummaryType(
          practitioner.providerType
        ),
      };

      const debounceGetProviderResult = debounce(getProviderResult, 250);
      debounceGetProviderResult({ variables });
      return;
    }

    const practitionerDetails = enableMapPinRedesign
      ? practitionerInResults
      : practitioner;

    if (mobileScreen) {
      setPopupContent({ ...practitionerDetails });
      return;
    }
    mapViewUpdatePin(
      practitionerDetails,
      flyToPin,
      popupContent,
      setPopupContent,
      map
    );
  };

  const enablePagination: boolean = totalResultsCount < pageSize + 1;

  const finalProviderResults = getFinalProviderResults(selectedItems, results);
  const filteredMapViewProviderWithAddress = finalProviderResults.filter(
    (provider) => provider.address?.line?.[0]
  );
  const shouldDisplayMap =
    expandedProviderListView && !mobileScreen
      ? navigateToDirections
        ? latitude && longitude && results?.length > 0
        : false
      : latitude && longitude && results?.length > 0;

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapKey = useConfig('MAPBOX_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');
  const stickyHeaderDisabled = portalData?.portalConfig?.stickyHeaderDisabled;

  const translatedText = getTranslatedText(heading, search, t);
  const isGEHAV2 = shouldUseGehaV2Styling();

  const handleCompareShareToggle = (isChecked: boolean) => {
    setOpenCompare(!compareShareToggle);
    setCompareShareToggle((prev) => !prev);
    if (!isChecked) {
      setSelectedItems([]);
      setSelectedCheckbox({ checked: {} });
      adobeModalTrackEvent('are you sure you want to exit');
    } else {
      adobeLinkTrackEvent({
        name: 'Compare and share a pdf',
        location: 'Toggle',
        type: 'Internal',
      });
    }
  };

  const handleClickLocation = async (locationId, locationLng, locationLat) => {
    setTimeout(async () => {
      await handleMapClickLocation(
        map.current,
        selectedId,
        longitude,
        latitude,
        locationId,
        locationLng,
        locationLat,
        mapOnPremKey,
        mapKey,
        mapOnPremURL,
        portalData?.portalConfig?.mapLocationUrl,
        mobileScreen,
        setDirections,
        setRouteEndCoords,
        setSelectedId,
        expandedProviderListView
      );
    }, 100);
  };

  useEffect(() => {
    if (mobileScreen) setMobileRouteView(!!routeEndCoords[0]);
  }, [routeEndCoords, mobileScreen]);

  useEffect(() => {
    const sessionDataBreadcrumbs = sessionData?.breadcrumbs;

    const sessionDataBreadcrumbKeys = Object.keys(
      sessionDataBreadcrumbs || {}
    ).filter(
      (key) => key !== ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key
    );

    const allBreadcrumbsMatch = sessionDataBreadcrumbKeys.every((key) =>
      breadcrumbSessionStorage?.[key]?.includes(sessionDataBreadcrumbs[key])
    );

    const sessionDataMapViewBreadcrumb =
      sessionDataBreadcrumbs?.[
        ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key
      ];

    const isMapViewBreadcrumbSame = (
      ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.path +
      window.location.search
    )?.includes(sessionDataMapViewBreadcrumb);

    if (sessionData && isMapViewBreadcrumbSame && allBreadcrumbsMatch) {
      setIsOpen(true);
      setDrawerContent(sessionData.drawerContent);
      setCurrentPage(sessionData.currentPage);
      setNavigatedFromDetails(true);
    }
  }, [sessionData]);

  const downloadButtonProps = {
    text: t('COMPARE_BUTTONS.DOWNLOAD_PRINT_RESULTS'),
    view: 'desktop',
    setOpenBoxContents,
    setOpenShare,
    setOpenCompare,
    setCompareShareToggle,
    setSelectedCheckbox,
    setSelectedItems,
    printPage: pageNumber,
    enableMapViewEnhancements,
    results,
    filterProviderResultsPrintViewProps,
    isClicked,
    setIsClicked,
  };
  const enhanceWrapper = useMemo(
    () => (
      <EnhanceStackWrapper
        cssProps={{
          urlCheck,
          enableMapViewEnhancements,
          choosePCP,
          enableMapFilterSortCTA,
          stickyHeaderDisabled,
          enableMapViewHideOption,
          expandedProviderListView,
          enableListViewHeaderSticky,
        }}
      >
        {isLoading && (
          <Layout.Stack
            alignItems="left"
            style={{ marginTop: '18px', width: '450px' }}
          >
            <Skeleton height={29} variant="rounded" width={200} />
            <Skeleton height={29} variant="rounded" width={150} />
            <Skeleton height={29} variant="rounded" width={325} />
          </Layout.Stack>
        )}
        {!isLoading &&
          !mobileScreen &&
          enableMapViewEnhancements &&
          (expandedProviderListView ? (
            <>
              {isValidCategory && (
                <SmartChoiceInfoChip
                  popoverContent={popoverContent ?? undefined}
                  wrapperStyle={{
                    paddingBottom: '-8px',
                    paddingTop: '16px',
                  }}
                />
              )}
              <Flex
                css={{
                  'abyss-flex-root': {
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #CBCCCD',
                    padding: '16px 12px',
                  },
                }}
              >
                <Text
                  css={{
                    'abyss-text-root': {
                      marginTop: '5px',
                      marginBottom: '8px',
                      display: 'flex',
                      fontSize: '16px',
                      lineHeight: '18px',
                      fontWeight: '500 !important',
                      color: '#323333',
                    },
                  }}
                  data-auto-testid="total-count-results-heading"
                  data-testid="total-count-results-heading"
                >
                  {`${totalResultsCount} ${t(
                    `${mapViewSectionType} results for`
                  )}`}
                  <Heading
                    css={{
                      'abyss-heading-root': {
                        fontSize: '16px !important',
                        lineHeight: '18px !important',
                        color: '#323333 !important',
                        width: 'auto',
                        paddingLeft: '4px',
                      },
                    }}
                    data-auto-testid="results-heading"
                    data-testid="results-heading"
                    display="h6"
                    id="map-view-results"
                    offset={4}
                    textAlign="left"
                  >
                    {translatedText}
                  </Heading>
                </Text>
                <Layout.Group
                  alignItems="center"
                  css={{
                    width: 'auto',
                    '.abyss-text-root': {
                      fontSize: '16px !important',
                    },
                  }}
                  style={{ gap: '12px' }}
                >
                  {sectionType === Constants.RESULT_SECTION.PROVIDER ? (
                    showMergedCompareAndShare ? (
                      <CompareShareToggle
                        data-testid="compare-share-toggle-map-view"
                        dataTestId="compare-share-toggle-map-view"
                        expandedProviderListView={expandedProviderListView}
                        isChecked={compareShareToggle}
                        onChange={(e) =>
                          handleCompareShareToggle(e.target.checked)
                        }
                        title={t('COMPARE_BUTTONS.COMPARE_AND_SHARE')}
                      />
                    ) : (
                      <>
                        <ShareCompareButton
                          enableMapViewEnhancements={enableMapViewEnhancements}
                          isShareButton={false}
                          openCompare={openCompare}
                          sectionType={sectionType}
                          setIsClicked={setIsClicked}
                          setOpenCompare={setOpenCompare}
                          setOpenShare={setOpenShare}
                          setSelectedCheckbox={setSelectedCheckbox}
                          setSelectedItems={setSelectedItems}
                          text={t('COMPARE_BUTTONS.COMPARE')}
                          view="desktop"
                        />
                        <ShareCompareButton
                          enableMapViewEnhancements={enableMapViewEnhancements}
                          openShare={openShare}
                          sectionType={sectionType}
                          setIsClicked={setIsClicked}
                          setOpenCompare={setOpenCompare}
                          setOpenShare={setOpenShare}
                          setSelectedCheckbox={setSelectedCheckbox}
                          setSelectedItems={setSelectedItems}
                          text={t('COMPARE_BUTTONS.SHARE')}
                          view="desktop"
                        />
                      </>
                    )
                  ) : (
                    ''
                  )}
                  <Layout.Group
                    css={{
                      flexDirection: 'row',
                    }}
                    space={4}
                  >
                    <Text>{t('SORT_BUTTONS.SORT')}: </Text>
                    <ResultSort
                      data-testid="list-view-result-sort"
                      enableMapViewHideOption={enableMapViewHideOption}
                      expandedProviderListView={expandedProviderListView}
                      isMapView
                      sectionType={sectionType}
                    />
                  </Layout.Group>
                </Layout.Group>
              </Flex>
            </>
          ) : (
            <Flex>
              <Text
                css={{
                  'abyss-text-root': {
                    color: isGEHAV2 ? '$primary1' : '',
                    marginTop: '24px',
                    marginBottom: '8px',
                  },
                }}
                data-auto-testid="total-count-results-heading"
                data-testid="total-count-results-heading"
              >
                {enableFontHeaderMapView ? (
                  mapViewHeaderLabel
                ) : !enablePagination ? (
                  <ResultCount
                    {...paginationProps}
                    css={{
                      'abyss-pagination-results-text': {
                        border: '0px',
                        margin: '0 2px ',
                        color: '#4B4D4F',
                        fontSize: '16px !important',
                        lineHeight: '20px !important',
                        fontWeight: '400 !important',
                      },
                    }}
                    customNoResultsLabel={t('PAGINATION.NO_RESULTS_LABEL')}
                    customOfLabel={t('PAGINATION.OF_LABEL')}
                    customResultLabel={t(`${mapViewSectionType} result for`)}
                    customResultsLabel={t(`${mapViewSectionType} results for`)}
                    customShowingLabel={' '}
                    pageSize={pageSize}
                    resultsTotalCount={totalResultsCount}
                  />
                ) : (
                  `${totalResultsCount} ${t('of')} ${totalResultsCount} ${t(
                    `${mapViewSectionType} results for`
                  )}`
                )}
              </Text>
              <MapViewHeaderEnhancement
                compareShareToggle={compareShareToggle}
                enableMapViewHideOption={enableMapViewHideOption}
                filterProviderResultsPrintViewProps={
                  filterProviderResultsPrintViewProps
                }
                heading={enableFontHeaderMapView ? ' ' : heading}
                openCompare={openCompare}
                openShare={openShare}
                pageNumber={pageNumber}
                pageSize={pageSize}
                search={enableFontHeaderMapView ? ' ' : search}
                sectionType={sectionType}
                setCompareShareToggle={setCompareShareToggle}
                setOpenBoxContents={setOpenBoxContents}
                setOpenCompare={setOpenCompare}
                setOpenShare={setOpenShare}
                setSelectedCheckbox={setSelectedCheckbox}
                setSelectedItems={setSelectedItems}
              />
            </Flex>
          ))}

        {!mobileScreen && !enableMapViewEnhancements && (
          <MapViewTopPagination
            downloadButtonProps={downloadButtonProps}
            enableMapViewEnhancements={enableMapViewEnhancements}
            enableMapViewHideOption={enableMapViewHideOption}
            expandedProviderListView={expandedProviderListView}
            gotoPages={gotoPageFunc}
            isLoading={isLoading}
            nextPageTop={nextPageTop}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            paginationProps={paginationProps}
            previousPageTop={previousPageTop}
            setPageSizeFunc={setPageSizeFunc}
            topPaginationRef={topPaginationRef}
          />
        )}
        {!closeSidePanel ? (
          <ListWrapColumn
            aria-labelledby="map-view-results"
            cssProps={{
              isOpenCompareShare,
              enableMapViewEnhancements,
              enableMapViewHideOption,
              expandedProviderListView,
            }}
            role="group"
          >
            {isLoading ? (
              <DataCardListStyled style={{ marginTop: '18px' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
                  <DataCardContainer
                    isLoading={isLoading}
                    isMNR={isMNR}
                    key={item}
                  />
                ))}
              </DataCardListStyled>
            ) : (
              <ListWrap
                cssProps={{
                  expandedProviderListView,
                  enableMapViewHideOption,
                }}
                ref={listRef}
              >
                {enableVirtualCareEpic &&
                  enableVirtualCareProvidersInMapResultPage &&
                  pageNumber === 1 &&
                  virtualCareProviders &&
                  Object.keys(virtualCareProviders).length > 0 && (
                    <VirtualCareProvidersBanner
                      expandedProviderListView={!!expandedProviderListView}
                      setVirtualCareListView={setIsVirtualCareListView}
                      virtualCareProviders={virtualCareProviders}
                    />
                  )}
                {!mobileScreen && !mobileRouteView && !mobileMixedResult && (
                  <DataCardList
                    DataCardStyle={{
                      width: dataCardCssWidth(
                        expandedProviderListView,
                        enableMapViewHideOption
                      ),
                      minHeight: dataCardMinHeight,
                    }}
                    addSelectedProvider={addSelectedProvider}
                    compareShareToggle={compareShareToggle}
                    expandedProviderListView={expandedProviderListView}
                    fromMobileListView={false}
                    headers={headers}
                    heading={heading}
                    isLoading={isLoading}
                    isMNR={isMNR}
                    isMobileMap={mobileScreen}
                    isTieredPlan={isTieredPlan}
                    locationForAnalytics={`${sectionType} results for ${search}`}
                    map={map}
                    navigateToDirections={navigateToDirections}
                    pageNumber={pageNumber}
                    parentRef={listRef}
                    providerResults={results}
                    recommendationPreferences={recommendationPreferences}
                    searchRadius={searchRadius}
                    searchTerm={searchTerm}
                    sectionType={sectionType}
                    selectLocation={handleClickLocation}
                    selectedCheckbox={selectedCheckbox}
                    selectedFilters={selectedFilters}
                    setIsCompareClicked={setIsCompareClicked}
                    setNavigateToDirections={setNavigateToDirections}
                    setRouteEndCoords={setRouteEndCoords}
                    setSelectedCheckbox={setSelectedCheckbox}
                    showCheckboxes={isOpenCompareShare}
                    updatePin={updatePin}
                  />
                )}
              </ListWrap>
            )}
            {!mobileScreen ? (
              <Flex
                css={
                  enableMapViewEnhancements
                    ? EnhancePaginationStyles(expandedProviderListView)
                    : {}
                }
              >
                <Flex
                  alignItems="center"
                  css={{ gap: '21px', marginTop: '16px', width: '408px' }}
                  direction="column"
                >
                  {enableMapViewEnhancements ? (
                    <Flex justify="center">
                      {!enablePagination && (
                        <MapViewTopPagination
                          downloadButtonProps={downloadButtonProps}
                          enableMapViewEnhancements={enableMapViewEnhancements}
                          enableMapViewHideOption={enableMapViewHideOption}
                          expandedProviderListView={expandedProviderListView}
                          gotoPages={gotoPageFunc}
                          isLoading={isLoading}
                          nextPageTop={nextPageTop}
                          pageSize={pageSize}
                          pageSizeOptions={pageSizeOptions}
                          paginationProps={paginationProps}
                          previousPageTop={previousPageTop}
                          setPageSizeFunc={setPageSizeFunc}
                          topPaginationRef={topPaginationRef}
                        />
                      )}
                      {enableMapViewHideOption && enablePagination && (
                        <DownloadResultsButton
                          {...downloadButtonProps}
                        ></DownloadResultsButton>
                      )}
                    </Flex>
                  ) : (
                    <MapDataPagination
                      {...paginationProps}
                      customNextLabel={t('PAGINATION.NEXT')}
                      customPreviousLabel={t('PAGINATION.PREVIOUS')}
                      data-auto-testid="pagination-component"
                      data-testid="pagination-component"
                      gotoPage={gotoPageFunc}
                      nextPage={nextPageBottom}
                      pageSize={pageSize}
                      previousPage={previousPageBottom}
                    />
                  )}
                </Flex>
              </Flex>
            ) : null}
          </ListWrapColumn>
        ) : null}
      </EnhanceStackWrapper>
    ),
    [
      urlCheck,
      enableMapViewEnhancements,
      choosePCP,
      enableMapFilterSortCTA,
      stickyHeaderDisabled,
      enableMapViewHideOption,
      expandedProviderListView,
      enableListViewHeaderSticky,
      isLoading,
      mobileScreen,
      expandedProviderListView,
      totalResultsCount,
      t,
      mapViewSectionType,
      translatedText,
      sectionType,
      showMergedCompareAndShare,
      compareShareToggle,
      handleCompareShareToggle,
      enableFontHeaderMapView,
      mapViewHeaderLabel,
      enablePagination,
      paginationProps,
      pageSize,
      filterProviderResultsPrintViewProps,
      heading,
      openCompare,
      openShare,
      setCompareShareToggle,
      setOpenBoxContents,
      setOpenCompare,
      setOpenShare,
      setSelectedCheckbox,
      setSelectedItems,
    ]
  );
  return (
    <React.Fragment>
      {isOpen && (
        <MapViewGroupedLocationDrawer
          coverageType={coverageType}
          drawerContent={drawerContent}
          isMNR={isMNR}
          isTieredPlan={isTieredPlan}
          navigatedFromDetails={navigatedFromDetails}
          openDrawer={isOpen}
          pageNumber={currentPage}
          pageSize={pageSize}
          providerToScrollTo={sessionData?.providerId}
          searchRadius={searchRadius}
          setNavigatedFromDetails={setNavigatedFromDetails}
          setOpenDrawer={setIsOpen}
          shouldGetHGData={SHOULD_GET_HG_DATA}
        />
      )}
      <MapViewHeader
        eapCodeFlag={eapCodeFlag}
        enableMapViewEnhancements={enableMapViewEnhancements}
        filterProviderResultsPrintViewProps={
          filterProviderResultsPrintViewProps
        }
        isBHCare={isBHCare}
        isPageTracked={isPageTracked}
        mobileScreen={mobileScreen}
        openCompare={openCompare}
        openShare={openShare}
        pageHeader={pageHeader}
        previousLocation={previousLocation}
        resultSet={results}
        sectionType={sectionType}
        setCompareShareToggle={setCompareShareToggle}
        setOpenBoxContents={setOpenBoxContents}
        setOpenCompare={setOpenCompare}
        setOpenShare={setOpenShare}
        setPreviousLocation={setPreviousLocation}
        setSelectedCheckbox={setSelectedCheckbox}
        setSelectedItems={setSelectedItems}
      />
      <MapviewWrapper
        cssProps={{
          enableMapViewEnhancements,
          isOpenCompareShare,
          navigateToDirections,
          openMapview,
          enableMapViewHideOption,
          expandedProviderListView,
        }}
      >
        {expandedProviderListView && navigateToDirections
          ? null
          : enhanceWrapper}
        <MapViewWrap
          cssProps={{
            enableMapViewEnhancements,
            urlCheck,
            choosePCP,
            enableMapFilterSortCTA,
            enableMapViewHideOption,
            expandedProviderListView,
          }}
        >
          {showMapCollapse ? (
            <SidePaneCloseButton
              sidePanelArrowIcon={sidePanelArrowIcon}
              toggleSidePanel={toggleSidePanel}
            />
          ) : null}
          {shouldDisplayMap ? (
            <MapStyleWrap>
              {mobileScreen && !mobileRouteView && popupContent ? (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '50px',
                    zIndex: 999,
                    left: '50%',
                    transform: 'translate(-50%)',
                  }}
                >
                  <BottomDataCard
                    headers={headers}
                    isMNR={isMNR}
                    locationForAnalytics={`map:${sectionType} results for ${search}`}
                    pageNumber={pageNumber}
                    practitioner={popupContent}
                    sectionType={sectionType}
                    selectLocation={handleClickLocation}
                    selectedFilters={selectedFilters}
                    setPopupContent={setPopupContent}
                  />
                </div>
              ) : null}
              <MapDisplay
                closeSidePanel={closeSidePanel}
                coords={{ latitude, longitude }}
                directions={directions}
                enableMapFilterSortCTA={enableMapFilterSortCTA}
                expandedProviderListView={expandedProviderListView}
                headers={headers}
                isOpenCompareShare={isOpenCompareShare}
                map={map}
                mobileMapControlChildren={
                  <MobileMapFilter
                    enableMapViewHideOption={enableMapViewHideOption}
                    openMapview={openMapview}
                    setDirections={setDirections}
                    setMobileRouteView={setMobileRouteView}
                    setOpenMapview={setOpenMapview}
                    setRouteEndCoords={setRouteEndCoords}
                    setSelectedId={setSelectedId}
                  />
                }
                mobileRouteView={mobileRouteView}
                mobileScreen={mobileScreen}
                previousLocation={previousLocation}
                providerPins={providerPins}
                providerResults={filteredMapViewProviderWithAddress}
                routeEndCoords={routeEndCoords}
                selectLocation={handleClickLocation}
                selectedItems={selectedItems}
                setDrawerContent={setDrawerContent}
                setExpandedProviderListView={setExpandedProviderListView}
                setGroupedLocationDrawerOpen={setIsOpen}
                setNavigateToDirections={setNavigateToDirections}
                setPopupContent={setPopupContent}
                setPreviousLocation={setPreviousLocation}
                setRouteEndCoords={setRouteEndCoords}
                updatePin={updatePin}
              />
            </MapStyleWrap>
          ) : null}
        </MapViewWrap>
      </MapviewWrapper>
      {!mobileScreen && (
        <React.Fragment>
          <Popup
            handleClickLocation={handleClickLocation}
            headers={headers}
            isMNR={isMNR}
            locationForAnalytics={`map:${sectionType} results for ${search}`}
            pageNumber={pageNumber}
            practitioner={popupContent}
            selectedFilters={selectedFilters}
            updatePin={updatePin}
          />
        </React.Fragment>
      )}
      {!showMergedCompareAndShare && (
        <ShowCompareFloatingSection
          compareShareToggle={compareShareToggle}
          containerRef={containerRef}
          headers={headers}
          items={items}
          map={map}
          mobileScreen={mobileScreen}
          openCompare={openCompare}
          recommendationPreferences={recommendationPreferences}
          removeItem={removeItem}
          selectedCheckbox={selectedCheckbox}
          selectedItems={selectedItems}
          setCompareShareToggle={setCompareShareToggle}
          setItems={setItems}
          setOpenCompare={setOpenCompare}
          setSelectedCheckbox={setSelectedCheckbox}
          setSelectedItems={setSelectedItems}
          showMergedCompareAndShare={showMergedCompareAndShare}
          urlCheck={urlCheck}
        />
      )}
    </React.Fragment>
  );
};
